import {
  type PlanDataType,
  type DispatchPlanFuelUsageMetricsResponseType,
  type DispatchPlanMaterialDumpResponseType,
  type DispatchPlanTravelTimeMetricsResponseType,
  type DispatchPlanTripsResponseType,
} from 'views/FleetPlanner/CreateDispatchPlan/Steps/types/steps.types';
import { type DispatchSinglePlanSummaryType } from '../types/dispatch-summary.types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(timezone);
dayjs.extend(utc);

function safeSum<T extends object>(
  data: T | undefined,
  keys: Array<keyof T>
): number {
  if (!data) return 0;

  return keys.reduce((sum, key) => {
    const value = (data as any)[key];
    return sum + (typeof value === 'number' ? value : 0);
  }, 0);
}

export const aggregatePlanMetricsTotals = (metrics: {
  fuel_usage: Record<string, DispatchPlanFuelUsageMetricsResponseType>;
  material_dumped: Record<string, DispatchPlanMaterialDumpResponseType>;
  travel_time: Record<string, DispatchPlanTravelTimeMetricsResponseType>;
  trips: Record<string, DispatchPlanTripsResponseType>;
}) => {
  const bumperNumbers = Object.keys(metrics.fuel_usage);

  let totalFuel = 0;
  let totalTons = 0;
  let totalMinutes = 0;

  for (const bumperNumber of bumperNumbers) {
    const fuelData = metrics.fuel_usage[bumperNumber];
    const materialData = metrics.material_dumped[bumperNumber];
    const timeData = metrics.travel_time[bumperNumber];

    if (!fuelData || !materialData || !timeData) continue;

    totalFuel += safeSum(fuelData, [
      'idling_fuel',
      'loading_fuel',
      'dumping_fuel',
      'moving_loaded_fuel',
      'moving_unloaded_fuel',
    ]);

    totalTons += materialData.total;

    totalMinutes += safeSum(timeData, [
      'idling_mins',
      'loading_mins',
      'dumping_mins',
      'moving_loaded_mins',
      'moving_unloaded_mins',
    ]);
  }

  const fuelPerTon = totalTons ? totalFuel / totalTons : 0;
  const productivity = totalMinutes ? totalTons / (totalMinutes / 60) : 0; // tons/hour

  return { totalFuel, totalTons, totalMinutes, fuelPerTon, productivity };
};

export const compareMasterAlgoVsApprovedPlanKPI = (
  actual: {
    fuel_usage: Record<string, DispatchPlanFuelUsageMetricsResponseType>;
    material_dumped: Record<string, DispatchPlanMaterialDumpResponseType>;
    travel_time: Record<string, DispatchPlanTravelTimeMetricsResponseType>;
    trips: Record<string, DispatchPlanTripsResponseType>;
  },
  approved: {
    fuel_usage: Record<string, DispatchPlanFuelUsageMetricsResponseType>;
    material_dumped: Record<string, DispatchPlanMaterialDumpResponseType>;
    travel_time: Record<string, DispatchPlanTravelTimeMetricsResponseType>;
    trips: Record<string, DispatchPlanTripsResponseType>;
  }
) => {
  const actualKPI = aggregatePlanMetricsTotals(actual);
  const approvedKPI = aggregatePlanMetricsTotals(approved);

  const deltaFuel = actualKPI.totalFuel - approvedKPI.totalFuel;
  const deltaTons = actualKPI.totalTons - approvedKPI.totalTons;
  const deltaMinutes = actualKPI.totalMinutes - approvedKPI.totalMinutes;
  const deltaFuelPerTon = actualKPI.fuelPerTon - approvedKPI.fuelPerTon;
  const deltaProductivity = actualKPI.productivity - approvedKPI.productivity;

  return {
    plan: approvedKPI,
    actual: actualKPI,
    deltas: {
      fuel: deltaFuel,
      tons: deltaTons,
      minutes: deltaMinutes,
      fuelPerTon: deltaFuelPerTon,
      productivity: deltaProductivity,
    },
  };
};

export const mergeDispatchPlans = (
  plans: DispatchSinglePlanSummaryType[] | undefined | null
): {
  planData: PlanDataType[];
  metrics: {
    travel_time: DispatchPlanTravelTimeMetricsResponseType[];
    fuel_usage: DispatchPlanFuelUsageMetricsResponseType[];
    material_dumped: DispatchPlanMaterialDumpResponseType[];
    trips: DispatchPlanTripsResponseType[];
  };
} => {
  const mergedPlanData: PlanDataType[] = [];

  const normalizeMetrics = <T>(
    metrics: T[] | Record<string, T> | undefined | null
  ): T[] => {
    if (!metrics) return [];

    if (Array.isArray(metrics)) {
      return metrics;
    }

    return Object.values(metrics);
  };

  const travelTimeMap = new Map<
    string,
    DispatchPlanTravelTimeMetricsResponseType
  >();
  const fuelUsageMap = new Map<
    string,
    DispatchPlanFuelUsageMetricsResponseType
  >();
  const materialDumpMap = new Map<
    string,
    DispatchPlanMaterialDumpResponseType
  >();
  const tripsMap = new Map<string, DispatchPlanTripsResponseType>();

  if (!Array.isArray(plans)) {
    return {
      planData: [],
      metrics: {
        travel_time: [],
        fuel_usage: [],
        material_dumped: [],
        trips: [],
      },
    };
  }

  for (const plan of plans) {
    if (!plan) continue;

    if (Array.isArray(plan.planData)) {
      mergedPlanData.push(...plan.planData);
    }

    const metrics = plan.metrics ?? {};

    // Travel time
    for (const metric of normalizeMetrics(metrics.travel_time) ?? []) {
      if (!metric?.bumper_number) continue;

      const existing = travelTimeMap.get(metric.bumper_number);
      if (existing) {
        existing.moving_loaded_mins += metric.moving_loaded_mins ?? 0;
        existing.moving_unloaded_mins += metric.moving_unloaded_mins ?? 0;
        existing.idling_mins += metric.idling_mins ?? 0;
        existing.loading_mins += metric.loading_mins ?? 0;
        existing.dumping_mins += metric.dumping_mins ?? 0;
      } else {
        travelTimeMap.set(metric.bumper_number, {
          bumper_number: metric.bumper_number,
          moving_loaded_mins: metric.moving_loaded_mins ?? 0,
          moving_unloaded_mins: metric.moving_unloaded_mins ?? 0,
          idling_mins: metric.idling_mins ?? 0,
          loading_mins: metric.loading_mins ?? 0,
          dumping_mins: metric.dumping_mins ?? 0,
        });
      }
    }

    // Fuel usage
    for (const metric of normalizeMetrics(metrics.fuel_usage) ?? []) {
      if (!metric?.bumper_number) continue;

      const existing = fuelUsageMap.get(metric.bumper_number);
      if (existing) {
        existing.moving_loaded_fuel += metric.moving_loaded_fuel ?? 0;
        existing.moving_unloaded_fuel += metric.moving_unloaded_fuel ?? 0;
        existing.idling_fuel += metric.idling_fuel ?? 0;
        existing.loading_fuel += metric.loading_fuel ?? 0;
        existing.dumping_fuel += metric.dumping_fuel ?? 0;
      } else {
        fuelUsageMap.set(metric.bumper_number, {
          bumper_number: metric.bumper_number,
          moving_loaded_fuel: metric.moving_loaded_fuel ?? 0,
          moving_unloaded_fuel: metric.moving_unloaded_fuel ?? 0,
          idling_fuel: metric.idling_fuel ?? 0,
          loading_fuel: metric.loading_fuel ?? 0,
          dumping_fuel: metric.dumping_fuel ?? 0,
        });
      }
    }

    // Material dumped
    for (const metric of normalizeMetrics(metrics.material_dumped) ?? []) {
      if (!metric?.bumper_number) continue;

      const existing = materialDumpMap.get(metric.bumper_number);

      if (existing) {
        existing.total += metric.total ?? 0;
      } else {
        materialDumpMap.set(metric.bumper_number, {
          bumper_number: metric.bumper_number,
          total: metric.total ?? 0,
        });
      }
    }

    // Trips
    for (const metric of normalizeMetrics(metrics.trips) ?? []) {
      if (!metric?.bumper_number) continue;

      const existing = tripsMap.get(metric.bumper_number);
      if (existing) {
        existing.total =
          Number(existing.total ?? 0) + Number(metric.total ?? 0);
      } else {
        tripsMap.set(metric.bumper_number, {
          bumper_number: metric.bumper_number,
          total: metric.total ?? 0,
        });
      }
    }
  }

  const sortedData = mergedPlanData?.sort(
    (a, b) => dayjs.utc(a.start_ts).valueOf() - dayjs.utc(b.start_ts).valueOf()
  );
  return {
    planData: sortedData,
    metrics: {
      travel_time: Array.from(travelTimeMap.values()),
      fuel_usage: Array.from(fuelUsageMap.values()),
      material_dumped: Array.from(materialDumpMap.values()),
      trips: Array.from(tripsMap.values()),
    },
  };
};
